import React, { useId, useState } from 'react';

import { Input } from '@flixbus/honeycomb-react';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';
import GroupValidationMessage from './GroupValidationMessage';
import { combineDateTime, splitTimestamp, toDateOnly } from '../utils/incidentTime';
import { isAtOrAfter } from '../utils/timestampOrder';

import styles from './PostIncidentDrivingInputs.module.css';

const COPY = CREATE_BREAKDOWN_FLOW_TEXT.postIncidentDriving;
const ERR = CREATE_BREAKDOWN_FLOW_TEXT.errors;

/**
 * Post-incident driving block — only captures `driving.fromTimestamp`.
 *
 * Splits date and time into two adjacent inputs; combines them into a single
 * `YYYY-MM-DDTHH:mm` timestamp for the parent leaf. The driver assigned to
 * `driving.crewMemberId` is derived in the parent leaf (e.g. from cushion
 * choice in busDeadhead, or from a replacement-driver picker in
 * fatigueReplacement / driverOutOfHoursReplacement). The downstream payload mapper
 * resolves the actual crewMemberId per branch.
 *
 * Enforces the chain tail `ETA <= driving`:
 *  - When `replacementEta` is provided, driving is validated against it and
 *    the message reads "cannot start before the replacement arrival (ETA)".
 *  - When `replacementEta` is empty, we fall back to `driving >= incident`
 *    with the "cannot start before the incident time" message.
 *
 * @param {{
 *   value: { fromTimestamp: string },
 *   incidentTimestamp?: string,
 *   replacementEta?: string,
 *   onChange: (next: { fromTimestamp: string }) => void,
 * }} props
 */
const PostIncidentDrivingInputs = ({
	value,
	incidentTimestamp = '',
	replacementEta = '',
	onChange,
}) => {
	const dateId = useId();
	const timeId = useId();

	const [initialDate, initialTime] = splitTimestamp(value?.fromTimestamp);
	const [date, setDate] = useState(initialDate);
	const [time, setTime] = useState(initialTime);

	const handleDateChange = (event) => {
		const nextDate = event.target.value;
		setDate(nextDate);
		onChange({ fromTimestamp: combineDateTime(nextDate, time) });
	};

	const handleTimeChange = (event) => {
		const nextTime = event.target.value;
		setTime(nextTime);
		onChange({ fromTimestamp: combineDateTime(date, nextTime) });
	};

	const currentTimestamp = combineDateTime(date, time);
	// Chain-aware validation: prefer ETA when set, otherwise baseline incident.
	const drivingBeforeEta =
		!!currentTimestamp && !!replacementEta && !isAtOrAfter(currentTimestamp, replacementEta);
	const drivingBeforeIncident =
		!replacementEta &&
		!!currentTimestamp &&
		!!incidentTimestamp &&
		!isAtOrAfter(currentTimestamp, incidentTimestamp);
	const drivingInvalid = drivingBeforeEta || drivingBeforeIncident;
	const groupError = drivingBeforeEta
		? ERR.drivingBeforeEta
		: drivingBeforeIncident
			? ERR.drivingBeforeIncident
			: undefined;
	const dateMin = toDateOnly(replacementEta) || toDateOnly(incidentTimestamp);

	return (
		<div className={styles.group}>
			<div className={styles.row}>
				<div className={styles.dateField}>
					<Input
						id={dateId}
						type="date"
						label={COPY.dateLabel}
						value={date}
						onChange={handleDateChange}
						min={dateMin}
						valid={drivingInvalid ? false : undefined}
						data-testid="post-incident-driving-date-input"
					/>
				</div>
				<div className={styles.timeField}>
					<Input
						id={timeId}
						type="time"
						aria-label={COPY.timeAriaLabel}
						value={time}
						onChange={handleTimeChange}
						valid={drivingInvalid ? false : undefined}
						data-testid="post-incident-driving-time-input"
					/>
				</div>
			</div>

			<GroupValidationMessage message={groupError} testId="post-incident-driving-group-error" />
		</div>
	);
};

export default PostIncidentDrivingInputs;
